import React, {useEffect, useState} from 'react';
import {observer} from 'mobx-react-lite';
import Skeleton from 'react-loading-skeleton';
import 'react-loading-skeleton/dist/skeleton.css';
import {User} from '../../../entity/model/user';
import {Project} from '../../../entity/model/project';
import {Link} from 'react-router-dom';
import {Organization} from '../../../entity/model/organization';
import {County} from '../../../entity/model/county';
import {getStatus, OrderStatus} from '../../../entity/enums/order-status';
import {useService} from '../../../infrastructure/service/service';
import {UserPermission} from '../../../entity/enums/user-permission';
import {ManageMode} from '../../../entity/enums/manage-mode';
import {ChartTitle, getStatusByChartTitle} from '../../../entity/enums/chart-title';
import Box from '@mui/material/Box';
import LoadingComponent from '../components/widget/LoadingComponent';
import SearchIcon from '@mui/icons-material/Search';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import {
  Button,
  Checkbox,
  Divider,
  IconButton,
  InputAdornment,
  Menu,
  MenuItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography
} from '@mui/material';
import TableSortLabel from '@mui/material/TableSortLabel';
import moment from 'moment';
import {SearchType} from '../../../entity/enums/search-type';
import StatusWidget from '../components/widget/StatusWidget';
import VisibilityIcon from '@mui/icons-material/Visibility';
import DeleteIcon from '@mui/icons-material/Delete';
import ConfirmDialog from '../components/widget/ConfirmDialog';
import TablePagination from '../components/widget/TablePagination';
import RefreshIcon from '@mui/icons-material/Refresh';
import {Form, Formik, FormikProps} from 'formik';
import {SearchOption} from '../../../entity/dto/search-dto';
import {SearchInputDto} from '../../../entity/dto/search-input-dto';
import {getProjectColumnHeader, ProjectColumn} from '../../../entity/enums/project-column';
import {SortType} from '../../../entity/enums/sort-type';
import {State} from '../../../entity/model/state';
import EntryDialog from '../components/project/EntryDialog';

import '../../../assets/css/style.css';

const CompletedProjects = (): JSX.Element => {
  const {userService, projectService, organizationService, countyService, searchService, stateService} = useService();

  const [currentUser, setCurrentUser] = useState<User | undefined>();
  const [projects, setProjects] = useState<Project[]>([]);
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [users, setUsers] = useState<User[]>([])
  const [counties, setCounties] = useState<County[]>([]);
  const [states, setStates] = useState<State[]>([]);

  const [trigger, setTrigger] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(true);
  const [refresh, setRefresh] = useState<boolean>(false);
  const [openConfirmDialog, setOpenConfirmDialog] = useState<boolean>(false);
  const [loadingConfirmBtn, setLoadingConfirmBtn] = useState<boolean>(false);
  const [selectedProject, setSelectedProject] = useState<Project>();
  const [selected, setSelected] = React.useState<readonly string[]>([]);
  const [menuElement, setMenuElement] = useState<null | HTMLElement>(null);
  const [totalPages, setTotalPages] = useState<number>(0);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [sortMap, setSortMap] = useState<Map<ProjectColumn, SortType>>(new Map([[ProjectColumn.DUE_DATE, SortType.DESC]]));
  const [openEntryDialog, setOpenEntryDialog] = useState<boolean>(false);
  const [searchInput, setSearchInput] = useState<string>('');

  const pageSize = 100;
  const tableHeaders = [
    'Order Number',
    'Parcel Count',
    'Status',
    'Due Date',
    'Searcher',
    'Search Type',
    'Companies',
    'Address',
    'State',
    'County'
  ];

  useEffect(() => {
    projectService.getCache() && projectService.setCache(false);
  }, []);

  useEffect(() => {
    const init = async (): Promise<void> => {
      setTrigger(false);
      setLoading(true);

      const user = await userService.getCurrentUser();
      setCurrentUser(user);

      setTrigger(true);

      const response = await projectService.searchProjects(user?.organizationID ?? '', currentPage, pageSize, undefined, getSearchOptions(), ProjectColumn.DUE_DATE, SortType.DESC);
      if (response && response.projects && response.pageSize > 0) {
        const lastPageSize = response.totalCount % response.pageSize;
        const pageCount = Math.trunc(response.totalCount / response.pageSize);

        setProjects(response.projects);
        setTotalPages((lastPageSize === 0 ? pageCount : pageCount + 1));
      }

      setLoading(false);

      const users = await userService.getAllUsers(user?.organizationID ?? '', false);
      setUsers(users);

      const organizations = await organizationService.getOrganizations(user?.organizationID);
      setOrganizations(organizations);

      const counties = await countyService.getCounties();
      setCounties(counties);

      const states = await stateService.getStates();
      setStates(states);
    }

    if (!projectService.getCache())
      init();
  }, [projectService.getCache()])

  const getSearchOptions = (): SearchOption[] => {
    return [
      {
        field: 'status',
        value: getStatusByChartTitle(ChartTitle.COMPLETED_ORDERS)
      }
    ];
  }

  const handleDeletePermission = (): boolean => {
    if (!currentUser)
      return false;

    if (currentUser.organizationID !== selectedProject?.createdByOrganizationID)
      return false;

    if (selectedProject?.status === getStatus(OrderStatus.ON_HOLD))
      return false;

    if (selectedProject)
      return userService.isPermitted(UserPermission.DELETE_PROJECT) && (currentUser.id === selectedProject.createdByUserID || userService.isAdminUser());

    return false;
  }

  const handleDeleteAllPermission = (project: Project): boolean => {
    if (!currentUser)
      return false;

    if (currentUser.organizationID !== project?.createdByOrganizationID)
      return false;

    if (project?.status === getStatus(OrderStatus.ON_HOLD))
      return false;

    if (project)
      return userService.isPermitted(UserPermission.DELETE_PROJECT) && (currentUser.id === project.createdByUserID || userService.isAdminUser());

    return false;
  }

  const handleDeleteOrder = (): void => {
    handleMenuClose();
    setOpenConfirmDialog(true)
  }

  const deleteProjects = async (): Promise<void> => {
    setLoadingConfirmBtn(true);

    if (selected.length > 1) {
      const selectedProjects = projects.filter((project: Project): boolean => selected.includes(project.id));

      for (let i = 0; i < selectedProjects.length; i++)
        await projectService.deleteProjects(selectedProjects[i].createdByOrganizationID, selectedProjects[i].id, i === selectedProjects.length - 1);

    } else
      await projectService.deleteProjects(selectedProject?.createdByOrganizationID ?? '', selectedProject?.id ?? '', true);

    setSelected([]);
    setLoadingConfirmBtn(false);
    setOpenConfirmDialog(false);
  }

  const handleSearch = async (event: React.KeyboardEvent<HTMLDivElement>, searchInput: string): Promise<void> => {
    if (event.key === 'Enter') {
      setLoading(true);
      setCurrentPage(1);

      const response = await projectService.searchProjects(currentUser?.organizationID ?? '', currentPage, pageSize, searchInput, getSearchOptions());
      if (response && response.projects) {
        const lastPageSize = response.totalCount % response.pageSize;
        const pageCount = Math.trunc(response.totalCount / response.pageSize);

        setProjects(response.projects);
        setTotalPages((lastPageSize === 0 ? pageCount : pageCount + 1));
      }

      setLoading(false);
    }
  }

  const handleSelectAll = (event: React.ChangeEvent<HTMLInputElement>): void => {
    if (event.target.checked) {
      const newSelected = projects.filter((project: Project): boolean => handleDeleteAllPermission(project)).map((project: Project): string => project.id);
      setSelected(newSelected);
      return;
    }

    setSelected([]);
  }

  const handleSelect = (event: React.MouseEvent<unknown>, id: string): void => {
    const selectedIndex = selected.indexOf(id);
    let newSelected: readonly string[] = [];

    if (selectedIndex === -1)
      newSelected = newSelected.concat(selected, id);
    else if (selectedIndex === 0)
      newSelected = newSelected.concat(selected.slice(1));
    else if (selectedIndex === selected.length - 1)
      newSelected = newSelected.concat(selected.slice(0, -1));
    else if (selectedIndex > 0)
      newSelected = newSelected.concat(selected.slice(0, selectedIndex), selected.slice(selectedIndex + 1));

    setSelected(newSelected);
  }

  const disableSelectAll = (): boolean => {
    for (const project of projects)
      if (handleDeleteAllPermission(project))
        return false;

    return true;
  }

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, project: Project): void => {
    setMenuElement(event.currentTarget);
    setSelectedProject(project);
  }

  const handleMenuClose = (): void => {
    setMenuElement(null);
  }

  const handlePaginationChange = async (event: React.ChangeEvent<unknown>, activePage: number): Promise<void> => {
    setLoading(true);
    setCurrentPage(activePage);

    const response = await projectService.searchProjects(currentUser?.organizationID ?? '', activePage, pageSize, searchInput, getSearchOptions());
    if (response && response.projects)
      setProjects(response.projects);

    setLoading(false);
  }

  const refreshPage = async (): Promise<void> => {
    setRefresh(true);

    const response = await projectService.searchProjects(currentUser?.organizationID ?? '', currentPage, pageSize, searchInput, getSearchOptions());
    if (response && response.projects) {
      const lastPageSize = response.totalCount % response.pageSize;
      const pageCount = Math.trunc(response.totalCount / response.pageSize);

      setProjects(response.projects);
      setTotalPages((lastPageSize === 0 ? pageCount : pageCount + 1));
    }

    setRefresh(false);
  }

  const getCompanyName= (organizations: Organization[], project: Project): string  => {
    const createdOrganizationName = organizations.find((organization: Organization): boolean => organization.id === project.createdByOrganizationID)?.name;
    const assignedOrganizationName = organizations.find((organization: Organization): boolean => organization.id === project.assignedToOrganizationID)?.name;

    if (project.assignedToOrganizationID === project.createdByOrganizationID)
      return createdOrganizationName ?? '';

    return createdOrganizationName + ' / ' + assignedOrganizationName;
  }

  const handleSort = async (orderColumn?: ProjectColumn): Promise<void> => {
    if (orderColumn) {
      setLoading(true);
      setCurrentPage(1);

      let sortDirection = SortType.ASC;
      const newMap = new Map();
      const sortItem = sortMap.get(orderColumn);

      if (sortItem)
        sortDirection = sortItem === SortType.ASC ? SortType.DESC : SortType.ASC;

      newMap.set(orderColumn, sortDirection);
      setSortMap(newMap);

      const response = await projectService.searchProjects(currentUser?.organizationID ?? '', currentPage, pageSize, searchInput, getSearchOptions(), orderColumn, sortDirection);
      if (response && response.projects) {
        const lastPageSize = response.totalCount % response.pageSize;
        const pageCount = Math.trunc(response.totalCount / response.pageSize);

        setProjects(response.projects);
        setTotalPages((lastPageSize === 0 ? pageCount : pageCount + 1));
      }

      setLoading(false);
    }
  }

  const getSortDirection = (header: string): 'asc' | 'desc' => {
    const orderColumn = getProjectColumnHeader(header);
    if (orderColumn) {
      const sortItem = sortMap.get(orderColumn);
      if (sortItem)
        return sortItem === SortType.ASC ? 'asc' : 'desc';
    }

    return 'desc';
  }

  const viewEntryDialog = (project: Project): void => {
    setSelectedProject(project);
    setOpenEntryDialog(true);
  }

  const renderTableRow = (project: Project, index: number): JSX.Element => {
    return (
      <TableRow
        hover
        role='checkbox'
        tabIndex={-1}
        key={project.id}
        selected={selected.includes(project.id)}
        className={'table-cell'}>
        <Box>
          <TableCell className={'no-wrap'} padding='checkbox'>
            <IconButton
              id={`btn-completed-project-menu-${index}`}
              className={`zitles-menu-trigger ${selected.length > 1 ? 'disabled' : ''}`}
              color= "inherit"
              disableRipple
              disableFocusRipple
              disabled={selected.length > 1}
              onClick={(event: React.MouseEvent<HTMLElement>): void => handleMenuOpen(event, project)}>
              <MoreHorizIcon />
            </IconButton>
            <Menu elevation={0} anchorEl={menuElement} open={Boolean(menuElement)} className={'zitles-menu'} onClose={handleMenuClose}>
              <MenuItem
                id={`menu-completed-project-view-${index}`}
                disableRipple
                className={'zitles-menu-item'}
                component={Link}
                to={`/organization/${selectedProject?.createdByOrganizationID}/title/${selectedProject?.titleIDs[0]}`}
                onClick={handleMenuClose}>
                <VisibilityIcon className={'zitles-icon'}/>
                <Typography className={'zitles-menu-item button'}>View</Typography>
              </MenuItem>
              {
                handleDeletePermission() &&
                  <Box>
                    <Divider/>
                    <MenuItem id={`menu-completed-project-delete-${index}`} disableRipple className={'zitles-menu-item'} onClick={handleDeleteOrder}>
                      <DeleteIcon className={'zitles-icon'}/>
                      <Typography className={'zitles-menu-item button'}>Delete</Typography>
                    </MenuItem>
                  </Box>
              }
            </Menu>
          </TableCell>
          <TableCell className={`no-wrap table-checkbox body table-cell-checkbox ${!handleDeleteAllPermission(project) ? 'disabled' : ''}`} padding={'none'}>
            <Checkbox
              id={`checkbox-completed-project-${index}`}
              disabled={!handleDeleteAllPermission(project)}
              checked={selected.includes(project.id)}
              onClick={(event: React.MouseEvent<HTMLButtonElement, MouseEvent>): void => handleSelect(event, project.id)}/>
          </TableCell>
        </Box>
        <TableCell className={'no-wrap'} align={'left'}>
          <Link
            id={`text-completed-project-name-${index}`}
            className={'table-cell-link'}
            to={`/organization/${project.createdByOrganizationID}/title/${project.titleIDs[0]}`}>
            {project.orderNumber}
          </Link>
        </TableCell>
        <TableCell id={`text-completed-project-count-${index}`} className={'no-wrap table-cell-text link'} align={'left'} onClick={(): void => viewEntryDialog(project)}>
          {project.titleIDs.length} Parcels
        </TableCell>
        <TableCell id={`text-completed-project-status-${index}`} className={'no-wrap'} align={'left'}>
          <StatusWidget
            status={Object.values(ChartTitle).find((chartTitle: ChartTitle): boolean => chartTitle === (OrderStatus[project.status as keyof typeof OrderStatus] as unknown)) || ChartTitle.COMPLETED_ORDERS}
            table/>
        </TableCell>
        <TableCell id={`text-completed-project-due-date-${index}`} className={'no-wrap table-cell-text'} align={'left'}>
          {
            moment.utc(project.dueDate).format('YYYY-MM-DD')
          }
        </TableCell>
        <TableCell id={`text-completed-project-abstractor-${index}`} className={'no-wrap table-cell-text'} align={'left'}>
          {
            users.length > 0
              ? userService.getDisplayName(false, project?.assignedToUserID)
              : <Skeleton/>
          }
        </TableCell>
        <TableCell id={`text-completed-project-search-type-${index}`} className={'no-wrap table-cell-text'} align={'left'}>
          {
            SearchType[project.searchType as keyof typeof SearchType]
          }
        </TableCell>
        <TableCell id={`text-completed-project-organization-${index}`} className={'no-wrap table-cell-text'} align={'left'}>
          {
            organizations.length > 0
              ? getCompanyName(organizations, project)
              : <Skeleton/>
          }
        </TableCell>
        <TableCell id={`text-completed-project-address-${index}`} className={'no-wrap table-cell-text'} align={'left'}>
          {
            project.address ?? 'Multiple'
          }
        </TableCell>
        <TableCell id={`text-completed-project-state-${index}`} className={'no-wrap table-cell-text'} align={'left'}>
          {
            states.length > 0
              ? project.stateID === null ? '' : states.find((state: State): boolean => state.id === project.stateID)?.shortName ?? ''
              : <Skeleton/>
          }
        </TableCell>
        <TableCell id={`text-completed-project-county-${index}`} className={'no-wrap table-cell-text'} align={'left'}>
          {
            counties.length > 0
              ? project.countyID === null ? '' : counties.find((county: County): boolean => county.id === project.countyID)?.name ?? ''
              : <Skeleton/>
          }
        </TableCell>
      </TableRow>
    );
  }

  return (
    <>
      {
        !loading
          ? <Box>
            {
              refresh &&
                <LoadingComponent/>
            }
            <Paper className={'table-container'} elevation={0}>
              <Box className={'toolbar-container'}>
                <Button
                  id={'btn-completed-project-refresh'}
                  className={'zitles-button zitles-table'}
                  variant='contained'
                  startIcon={<RefreshIcon />}
                  disabled={refresh}
                  onClick={refreshPage}>
                  Refresh
                </Button>
                <Formik initialValues={{...searchService.getSearchInputDto(), searchInput}} enableReinitialize onSubmit={(): void => {}}>
                  {({values, handleChange}: FormikProps<SearchInputDto>): JSX.Element => (
                    <Form>
                      <TextField
                        id={'completed-project-search-bar'}
                        name='searchInput'
                        className={'zitles-searchbar'}
                        variant={'outlined'}
                        placeholder='Search'
                        onChange={(event: React.ChangeEvent<HTMLInputElement>): void => {
                          handleChange(event);
                          setSearchInput(event.target.value);
                        }}
                        onKeyDown={(event: React.KeyboardEvent<HTMLDivElement>): Promise<void> => handleSearch(event, values.searchInput)}
                        value={values.searchInput}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position='start'>
                              <SearchIcon className={'zitles-searchbar icon'}/>
                            </InputAdornment>)
                        }}/>
                    </Form>
                  )}
                </Formik>
                {
                  selected.length > 1 &&
                    <Button
                      id={'btn-delete-orders'}
                      disableElevation
                      disableRipple
                      className={'toolbar-button center-end'}
                      variant='contained'
                      startIcon={<DeleteIcon />}
                      onClick={(): void => setOpenConfirmDialog(true)}>
                      Delete
                    </Button>
                }
              </Box>
              {
                trigger
                  ? !loading && projects.length > 0
                    ? <TableContainer className={'zitles-table'} component={Paper}>
                        <Table>
                          <TableHead>
                            <TableRow className={'table-header'}>
                              <Box>
                                <TableCell className={'no-wrap'} padding='checkbox'/>
                                <TableCell className={'no-wrap table-cell-checkbox'} padding={'none'}>
                                  <Checkbox
                                    id={'completed-project-table-checkbox'}
                                    className={`table-checkbox header ${disableSelectAll() ? 'disabled' : ''}`}
                                    indeterminate={selected.length > 0 && selected.length < projects.filter((project: Project): boolean => handleDeleteAllPermission(project)).length}
                                    checked={!disableSelectAll() && projects.length > 0 && selected.length === projects.filter((project: Project): boolean => handleDeleteAllPermission(project)).length}
                                    onChange={handleSelectAll}
                                    disabled={disableSelectAll()}/>
                                </TableCell>
                              </Box>
                              {
                                tableHeaders.map((header: string, index: number): JSX.Element => (
                                  <TableCell key={index} className={'no-wrap'} align='left' padding='normal'>
                                    <TableSortLabel
                                      id={`completed-project-column-${header}`}
                                      className={'table-header-text'}
                                      active={true}
                                      direction={getSortDirection(header)}
                                      onClick={(): Promise<void> => handleSort(getProjectColumnHeader(header))}>
                                      {header}
                                    </TableSortLabel>
                                  </TableCell>
                                ))
                              }
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {
                              projects.map(renderTableRow)
                            }
                          </TableBody>
                        </Table>
                      </TableContainer>
                    : <Typography id={'completed-project-table-empty-prompt'} className='empty-message'>Create a template and new file to get started!</Typography>
                  : loading &&
                    <LoadingComponent/>
              }
            </Paper>
            {
              trigger && !loading && projects.length > 0 &&
                <TablePagination
                  currentPage={currentPage}
                  totalPages={totalPages}
                  onChange={handlePaginationChange}/>
            }
          </Box>
          : <LoadingComponent/>
      }
      {
        openConfirmDialog &&
          <ConfirmDialog
            open={openConfirmDialog}
            setOpen={setOpenConfirmDialog}
            title={`Delete File${selected.length > 1 ? 's' : ''}`}
            message={`Are you sure you want to delete ${selected.length > 1 ? selected.length + ' files?' : 'this file?'}`}
            handleSubmit={deleteProjects}
            loading={loadingConfirmBtn}/>
      }
      {
        openEntryDialog && selectedProject &&
          <EntryDialog
            open={openEntryDialog}
            setOpen={setOpenEntryDialog}
            mode={ManageMode.VIEW}
            project={selectedProject}/>
      }
    </>
  )
}

export default observer(CompletedProjects);
